/* @ts-nocheck */
/* global google */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PathLayer, TextLayer } from '@deck.gl/layers';
import { supabase } from '../../utils/supabaseClient';
import { useUser } from '../../context/UserContext';
import {
  MapIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  ArrowsPointingOutIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import ViewToggle from './ViewToggle';

const LIBRARIES = ['geometry'];
const mapContainerStyle = { width: '100%', height: '100%' };

const INITIAL_CENTER = { lat: 39.5, lng: -98.35 };
const INITIAL_ZOOM = 4;

const LABEL_ZOOM_THRESHOLD = 11;

const PROJECT_PALETTE = [
  { stroke: [14, 165, 233],  hex: '#0ea5e9' },
  { stroke: [236, 72, 153],  hex: '#ec4899' },
  { stroke: [132, 204, 22],  hex: '#84cc16' },
  { stroke: [249, 115, 22],  hex: '#f97316' },
  { stroke: [217, 70, 239],  hex: '#d946ef' },
  { stroke: [6, 182, 212],   hex: '#06b6d4' },
  { stroke: [168, 85, 247],  hex: '#a855f7' },
  { stroke: [225, 29, 72],   hex: '#e11d48' },
  { stroke: [20, 184, 166],  hex: '#14b8a6' },
  { stroke: [245, 158, 11],  hex: '#f59e0b' },
];

function colorForProject(projectId) {
  if (!projectId) return PROJECT_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    hash = ((hash << 5) - hash) + projectId.charCodeAt(i);
    hash |= 0;
  }
  return PROJECT_PALETTE[Math.abs(hash) % PROJECT_PALETTE.length];
}

function formatRelativeDate(dateStr) {
  if (!dateStr) return 'No date';
  const ageDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (ageDays === 0) return 'Today';
  if (ageDays === 1) return 'Yesterday';
  if (ageDays < 7) return `${ageDays}d ago`;
  if (ageDays < 30) return `${Math.floor(ageDays / 7)}w ago`;
  if (ageDays < 365) return `${Math.floor(ageDays / 30)}mo ago`;
  return `${Math.floor(ageDays / 365)}y ago`;
}

function geojsonToPaths(g) {
  if (!g) return [];
  if (g.type === 'LineString') return [g.coordinates];
  if (g.type === 'MultiLineString') return g.coordinates;
  return [];
}

// Date range options for the filter pills.
const RANGE_OPTIONS = [
  { key: 'all', label: 'All',  days: null },
  { key: '1y',  label: '1y',   days: 365 },
  { key: '90d', label: '90d',  days: 90 },
  { key: '30d', label: '30d',  days: 30 },
];

function rangeCutoff(rangeKey) {
  const opt = RANGE_OPTIONS.find(r => r.key === rangeKey);
  if (!opt || opt.days == null) return null;
  return Date.now() - opt.days * 86400000;
}

export default function MapView({ view, onViewChange }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentCompany } = useUser();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredSession, setHoveredSession] = useState(null);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [mapStyleMode, setMapStyleMode] = useState('roadmap');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mapRef = useRef(null);
  const deckOverlayRef = useRef(null);

  // Date range lives in URL so refresh / share preserves it.
  const rangeKey = searchParams.get('range') || 'all';
  const validRangeKey = RANGE_OPTIONS.some(r => r.key === rangeKey) ? rangeKey : 'all';

  const handleRangeChange = useCallback((key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'all') params.delete('range');
    else params.set('range', key);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    keyboardShortcuts: false,
    clickableIcons: false,
    mapTypeId: mapStyleMode === 'satellite' ? 'satellite' : 'roadmap',
    minZoom: 3,
    gestureHandling: 'greedy',
    zoomControl: false,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
  }), [mapStyleMode]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  useEffect(() => {
    if (!currentCompany?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error: rpcError } = await supabase
        .rpc('get_company_sessions_coverage', { company_id_input: currentCompany.id });
      if (cancelled) return;
      if (rpcError) {
        setError(rpcError.message);
        setLoading(false);
        return;
      }
      setSessions(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [currentCompany?.id]);

  // Filtered sessions used for map rendering — applies date range filter.
  const filteredSessions = useMemo(() => {
    const cutoff = rangeCutoff(validRangeKey);
    if (cutoff == null) return sessions;
    return sessions.filter(s => s.run_date && new Date(s.run_date).getTime() >= cutoff);
  }, [sessions, validRangeKey]);

  const pathRecords = useMemo(() => {
    const out = [];
    filteredSessions.forEach(s => {
      const color = colorForProject(s.project_id);
      geojsonToPaths(s.path_geojson).forEach(path => {
        out.push({
          session_id: s.session_id,
          session_name: s.session_name,
          folder_name: s.folder_name,
          project_id: s.project_id,
          project_name: s.project_name,
          run_date: s.run_date,
          colorStroke: color.stroke,
          hex: color.hex,
          path,
        });
      });
    });
    return out;
  }, [filteredSessions]);

  // Visible project labels for the map (aggregated from filteredSessions).
  const visibleProjectLabels = useMemo(() => {
    const byProject = new Map();
    filteredSessions.forEach(s => {
      if (!s.project_id) return;
      if (!byProject.has(s.project_id)) {
        byProject.set(s.project_id, {
          project_id: s.project_id,
          project_name: s.project_name,
          color: colorForProject(s.project_id),
          sessionCount: 0,
          minLat: Infinity, maxLat: -Infinity, minLng: Infinity, maxLng: -Infinity,
        });
      }
      const agg = byProject.get(s.project_id);
      agg.sessionCount += 1;
      if (s.min_lat != null && s.min_lat < agg.minLat) agg.minLat = s.min_lat;
      if (s.max_lat != null && s.max_lat > agg.maxLat) agg.maxLat = s.max_lat;
      if (s.min_lng != null && s.min_lng < agg.minLng) agg.minLng = s.min_lng;
      if (s.max_lng != null && s.max_lng > agg.maxLng) agg.maxLng = s.max_lng;
    });
    return Array.from(byProject.values())
      .filter(p => p.minLat !== Infinity)
      .map(p => ({ ...p, centroid: [(p.minLng + p.maxLng) / 2, (p.minLat + p.maxLat) / 2] }));
  }, [filteredSessions]);

  // Complete project list for the sidebar — unaffected by filters so users
  // can unhide projects they've filtered out.
  const allProjects = useMemo(() => {
    const byProject = new Map();
    sessions.forEach(s => {
      if (!s.project_id) return;
      if (!byProject.has(s.project_id)) {
        byProject.set(s.project_id, {
          project_id: s.project_id,
          project_name: s.project_name,
          color: colorForProject(s.project_id),
          sessionCount: 0,
          lastActivity: null,
          minLat: Infinity, maxLat: -Infinity, minLng: Infinity, maxLng: -Infinity,
        });
      }
      const agg = byProject.get(s.project_id);
      agg.sessionCount += 1;
      if (s.run_date && (!agg.lastActivity || s.run_date > agg.lastActivity)) {
        agg.lastActivity = s.run_date;
      }
      if (s.min_lat != null && s.min_lat < agg.minLat) agg.minLat = s.min_lat;
      if (s.max_lat != null && s.max_lat > agg.maxLat) agg.maxLat = s.max_lat;
      if (s.min_lng != null && s.min_lng < agg.minLng) agg.minLng = s.min_lng;
      if (s.max_lng != null && s.max_lng > agg.maxLng) agg.maxLng = s.max_lng;
    });
    return Array.from(byProject.values())
      .sort((a, b) => {
        // Most recent activity first; unknown dates last.
        if (!a.lastActivity && !b.lastActivity) return 0;
        if (!a.lastActivity) return 1;
        if (!b.lastActivity) return -1;
        return b.lastActivity.localeCompare(a.lastActivity);
      });
  }, [sessions]);

  const bounds = useMemo(() => {
    if (filteredSessions.length === 0) return null;
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    filteredSessions.forEach(s => {
      if (s.min_lat != null && s.min_lat < minLat) minLat = s.min_lat;
      if (s.max_lat != null && s.max_lat > maxLat) maxLat = s.max_lat;
      if (s.min_lng != null && s.min_lng < minLng) minLng = s.min_lng;
      if (s.max_lng != null && s.max_lng > maxLng) maxLng = s.max_lng;
    });
    if (minLat === Infinity) return null;
    return { minLat, maxLat, minLng, maxLng };
  }, [filteredSessions]);

  const handleSessionClick = useCallback((sessionId) => {
    if (!sessionId) return;
    navigate(`/session/${sessionId}`);
  }, [navigate]);

  const handleProjectClick = useCallback((projectId) => {
    if (!projectId) return;
    navigate(`/projects/${projectId}`);
  }, [navigate]);

  const flyToProject = useCallback((projectId) => {
    const p = allProjects.find(r => r.project_id === projectId);
    if (!p || !mapRef.current || p.minLat === Infinity) return;
    const b = new google.maps.LatLngBounds(
      { lat: p.minLat, lng: p.minLng },
      { lat: p.maxLat, lng: p.maxLng }
    );
    mapRef.current.fitBounds(b, 80);
  }, [allProjects]);

  const fitAllBounds = useCallback(() => {
    if (!mapRef.current || !bounds) return;
    const b = new google.maps.LatLngBounds(
      { lat: bounds.minLat, lng: bounds.minLng },
      { lat: bounds.maxLat, lng: bounds.maxLng }
    );
    mapRef.current.fitBounds(b, 60);
  }, [bounds]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;

    const overlay = new GoogleMapsOverlay({
      layers: [],
      interleaved: false,
      useDevicePixels: true,
    });
    overlay.setMap(map);
    deckOverlayRef.current = overlay;

    map.addListener('zoom_changed', () => setZoom(map.getZoom()));
    if (bounds) {
      const b = new google.maps.LatLngBounds(
        { lat: bounds.minLat, lng: bounds.minLng },
        { lat: bounds.maxLat, lng: bounds.maxLng }
      );
      map.fitBounds(b, 60);
    }
    google.maps.event.addListenerOnce(map, 'idle', () => setZoom(map.getZoom()));
  }, [bounds]);

  const onMapUnmount = useCallback(() => {
    if (deckOverlayRef.current) {
      deckOverlayRef.current.setMap(null);
      deckOverlayRef.current.finalize();
      deckOverlayRef.current = null;
    }
    mapRef.current = null;
  }, []);

  useEffect(() => {
    if (!deckOverlayRef.current) return;

    const isPathHighlighted = (d) =>
      hoveredSession === d.session_id || hoveredProject === d.project_id;

    const layers = [
      new PathLayer({
        id: 'session-paths',
        data: pathRecords,
        getPath: d => d.path,
        getColor: d => [...d.colorStroke, isPathHighlighted(d) ? 255 : 220],
        getWidth: d => (isPathHighlighted(d) ? 6 : 3),
        widthUnits: 'pixels',
        capRounded: true,
        jointRounded: true,
        pickable: true,
        onHover: info => setHoveredSession(info?.object?.session_id ?? null),
        onClick: info => handleSessionClick(info?.object?.session_id),
        updateTriggers: {
          getColor: [hoveredSession, hoveredProject],
          getWidth: [hoveredSession, hoveredProject],
        },
      }),
    ];

    if (zoom != null && zoom <= LABEL_ZOOM_THRESHOLD && visibleProjectLabels.length > 0) {
      layers.push(new TextLayer({
        id: 'project-labels',
        data: visibleProjectLabels,
        getPosition: d => d.centroid,
        getText: d => `${d.project_name || 'Unnamed'}  (${d.sessionCount})`,
        getSize: d => (hoveredProject === d.project_id ? 15 : 13),
        getColor: [17, 24, 39, 255],
        getBackgroundColor: d => (
          hoveredProject === d.project_id ? [255, 255, 255, 255] : [255, 255, 255, 240]
        ),
        getBorderColor: d => [...d.color.stroke, 255],
        getBorderWidth: d => (hoveredProject === d.project_id ? 2 : 1),
        background: true,
        backgroundPadding: [8, 5, 8, 5],
        fontFamily: 'Inter, system-ui, sans-serif',
        fontWeight: 600,
        billboard: true,
        pickable: true,
        onHover: info => setHoveredProject(info?.object?.project_id ?? null),
        onClick: info => handleProjectClick(info?.object?.project_id),
        updateTriggers: {
          getSize: hoveredProject,
          getBackgroundColor: hoveredProject,
          getBorderWidth: hoveredProject,
        },
      }));
    }

    deckOverlayRef.current.setProps({ layers });
  }, [pathRecords, visibleProjectLabels, hoveredSession, hoveredProject, zoom, handleSessionClick, handleProjectClick]);

  const showMap = isLoaded && !loading && !error;
  const visibleSessionCount = filteredSessions.length;
  const visibleProjectCount = visibleProjectLabels.length;
  const headerTitle = loading
    ? 'Loading…'
    : `${visibleSessionCount} session${visibleSessionCount === 1 ? '' : 's'} · ${visibleProjectCount} project${visibleProjectCount === 1 ? '' : 's'}`;

  return (
    <div className="h-screen flex flex-col bg-white">
      <div className="flex-shrink-0 h-14 border-b border-gray-200 px-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-gray-900 truncate">{headerTitle}</h1>

        <div className="flex items-center gap-3 flex-shrink-0">
          <DateRangePills value={validRangeKey} onChange={handleRangeChange} />
          <button
            onClick={() => setSidebarOpen(v => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded border transition-colors ${
              sidebarOpen
                ? 'border-gray-300 bg-gray-100 text-gray-900'
                : 'border-gray-300 text-gray-600 hover:bg-gray-100'
            }`}
            title="Projects"
          >
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
            Projects
          </button>
          <ViewToggle view={view} onChange={onViewChange} />
        </div>
      </div>

      <div className="flex-1 relative">
        {loadError && <ErrorState message="Failed to load Google Maps" />}
        {error && <ErrorState message={error} />}
        {!loadError && !error && (!isLoaded || loading) && (
          <LoadingOverlay label={isLoaded ? 'Loading sessions…' : 'Loading map…'} />
        )}
        {showMap && sessions.length === 0 && <EmptyState />}
        {showMap && sessions.length > 0 && filteredSessions.length === 0 && <NoMatchState />}

        {showMap && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            options={mapOptions}
            onLoad={onMapLoad}
            onUnmount={onMapUnmount}
            center={INITIAL_CENTER}
            zoom={INITIAL_ZOOM}
          />
        )}

        {showMap && (
          <MapControls
            mode={mapStyleMode}
            onChange={setMapStyleMode}
            onFitAll={fitAllBounds}
            canFit={!!bounds}
          />
        )}

        {hoveredProject && (() => {
          const p = visibleProjectLabels.find(row => row.project_id === hoveredProject);
          if (!p) return null;
          return (
            <div className="absolute bottom-4 left-4 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 pointer-events-none max-w-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color.hex }} />
                <span className="text-sm font-medium text-gray-900 truncate">
                  {p.project_name || 'Unnamed Project'}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                {p.sessionCount} session{p.sessionCount === 1 ? '' : 's'}
              </div>
              <div className="text-[11px] text-gray-400 mt-1">Click to open project</div>
            </div>
          );
        })()}

        {!hoveredProject && hoveredSession && (() => {
          const s = sessions.find(row => row.session_id === hoveredSession);
          if (!s) return null;
          const color = colorForProject(s.project_id);
          return (
            <div className="absolute bottom-4 left-4 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 pointer-events-none max-w-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color.hex }} />
                <span className="text-sm font-medium text-gray-900 truncate">
                  {s.session_name || 'Unnamed Session'}
                </span>
              </div>
              <div className="text-xs text-gray-500 flex items-center gap-1.5 flex-wrap">
                {s.folder_name && <span>{s.folder_name}</span>}
                {s.folder_name && <span className="text-gray-300">·</span>}
                <span className="text-gray-400">{s.project_name || 'No project'}</span>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {formatRelativeDate(s.run_date)}
              </div>
              <div className="text-[11px] text-gray-400 mt-1">Click to open session</div>
            </div>
          );
        })()}

        <ProjectSidebar
          open={sidebarOpen}
          projects={allProjects}
          onFlyTo={flyToProject}
          onOpenProject={handleProjectClick}
          onClose={() => setSidebarOpen(false)}
        />
      </div>
    </div>
  );
}

// ---------- Sub-components ----------

function DateRangePills({ value, onChange }) {
  return (
    <div className="inline-flex rounded border border-gray-300 divide-x divide-gray-300">
      {RANGE_OPTIONS.map((r, i) => {
        const active = value === r.key;
        const rounding = i === 0
          ? 'rounded-l'
          : i === RANGE_OPTIONS.length - 1
            ? 'rounded-r'
            : '';
        return (
          <button
            key={r.key}
            onClick={() => onChange(r.key)}
            className={`px-3 py-1 text-sm font-medium transition-colors ${rounding} ${
              active
                ? 'bg-gray-100 text-gray-900'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function ProjectSidebar({ open, projects, onFlyTo, onOpenProject, onClose }) {
  return (
    <div
      aria-hidden={!open}
      className={`absolute top-0 right-0 h-full w-80 bg-white border-l border-gray-200 shadow-xl z-[1000] flex flex-col transform transition-transform duration-200 ease-out ${
        open ? 'translate-x-0' : 'translate-x-full pointer-events-none'
      }`}
    >
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Projects</h3>
          <p className="text-xs text-gray-500">{projects.length} total</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {projects.length === 0 ? (
          <div className="p-6 text-center text-xs text-gray-400">
            No projects to show.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {projects.map(p => (
              <li
                key={p.project_id}
                className="group flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                onClick={() => onFlyTo(p.project_id)}
                title="Click to fly to project"
              >
                <span
                  className="w-1 self-stretch rounded-full flex-shrink-0"
                  style={{ backgroundColor: p.color.hex }}
                />
                <div className="flex-1 min-w-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenProject(p.project_id);
                    }}
                    className="inline-flex items-center gap-1 text-sm font-medium text-gray-900 hover:text-red-600 hover:underline truncate max-w-full"
                    title="Open project"
                  >
                    <span className="truncate">{p.project_name || 'Unnamed'}</span>
                    <ArrowTopRightOnSquareIcon className="h-3 w-3 flex-shrink-0 opacity-0 group-hover:opacity-60" />
                  </button>
                  <div className="text-[11px] text-gray-500">
                    {p.sessionCount} session{p.sessionCount === 1 ? '' : 's'} · {formatRelativeDate(p.lastActivity)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MapControls({ mode, onChange, onFitAll, canFit }) {
  return (
    <div className="absolute top-3 left-3 z-[1001]" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center space-x-1 p-1 bg-white border border-gray-200 rounded-lg shadow-lg">
        <button
          onClick={() => onChange('satellite')}
          className={`px-2.5 py-1.5 text-xs font-semibold rounded transition-colors ${
            mode === 'satellite' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Satellite
        </button>
        <button
          onClick={() => onChange('roadmap')}
          className={`px-2.5 py-1.5 text-xs font-semibold rounded transition-colors ${
            mode === 'roadmap' ? 'bg-red-600 text-white' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Map
        </button>
        <span className="w-px h-5 bg-gray-200 mx-0.5" />
        <button
          onClick={onFitAll}
          disabled={!canFit}
          title="Fit all visible projects"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
        >
          <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
          Fit
        </button>
      </div>
    </div>
  );
}

function LoadingOverlay({ label }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-3" />
        <p className="text-sm text-gray-600">{label}</p>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
      <div className="bg-white/90 border border-gray-200 rounded-lg px-6 py-5 text-center max-w-sm">
        <MapIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <h3 className="text-sm font-semibold text-gray-900 mb-1">No coverage yet</h3>
        <p className="text-xs text-gray-500">
          Sessions appear here once uploaded and processed.
        </p>
      </div>
    </div>
  );
}

function NoMatchState() {
  return (
    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
      <div className="bg-white/90 border border-gray-200 rounded-lg px-6 py-5 text-center max-w-sm">
        <MapIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <h3 className="text-sm font-semibold text-gray-900 mb-1">No sessions match</h3>
        <p className="text-xs text-gray-500">
          Try a wider date range.
        </p>
      </div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center z-10">
      <div className="bg-red-50 border border-red-200 rounded-lg px-6 py-5 text-center max-w-sm">
        <h3 className="text-sm font-semibold text-red-800 mb-1">Couldn't load map</h3>
        <p className="text-xs text-red-700">{message}</p>
      </div>
    </div>
  );
}
